import { create } from 'zustand';
import { USER_ROLES, type UserRole, isSubOrSuperAdmin } from '@/lib/constants/roles';
import { clearStack, hasStackedSession, popPrevious, pushAndSwitchTo } from './session-stack';
import { authStrategy, type UserPayload } from './strategy';

interface AuthState {
  userId: string | null;
  role: UserRole | null;
  /** Display fields filled in once the profile query resolves. */
  username: string | null;
  name: string | null;
  /** True iff a previous session is stashed (user-switch can be popped). */
  hasStack: boolean;

  isAuthenticated: () => boolean;
  isSubSuperAdmin: () => boolean;
  isSubOrSuperAdmin: () => boolean;

  /** Called after a successful POST /api/obd/login. */
  login: (payload: { user: UserPayload; token: string | null }) => void;
  /** Patch profile fields once /api/obd/user/profile resolves. */
  hydrateProfile: (profile: { username?: string; name?: string }) => void;
  /** Clear local state and authStrategy storage. */
  logout: () => void;
  /** Pop a stacked session if one exists; returns true if popped. */
  popSession: () => boolean;
  /** Push current and switch identity. */
  switchTo: (payload: { user: UserPayload; token: string | null }) => void;
}

function hydrate(): Pick<AuthState, 'userId' | 'role' | 'username' | 'name' | 'hasStack'> {
  const user = authStrategy.getUser();
  return {
    userId: user?.userId ?? null,
    role: (user?.role as UserRole | undefined) ?? null,
    username: user?.username ?? null,
    name: user?.name ?? null,
    hasStack: hasStackedSession(),
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ...hydrate(),

  isAuthenticated: () => authStrategy.isAuthenticated() && Boolean(get().userId),
  isSubSuperAdmin: () => get().role === USER_ROLES.SUB_SUPER_ADMIN,
  isSubOrSuperAdmin: () => isSubOrSuperAdmin(get().role),

  login: ({ user, token }) => {
    authStrategy.setSession({ user, token });
    set({
      userId: user.userId,
      role: user.role as UserRole,
      username: user.username ?? null,
      name: user.name ?? null,
      hasStack: hasStackedSession(),
    });
  },

  hydrateProfile: ({ username, name }) => {
    set((s) => ({
      username: username ?? s.username,
      name: name ?? s.name,
    }));
  },

  logout: () => {
    authStrategy.clearSession();
    clearStack();
      // Clear flash banner session flags so they show again on next login
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('flash_shown:')) sessionStorage.removeItem(key);
    });
    set({ userId: null, role: null, username: null, name: null, hasStack: false });
    // Clear the persisted theme so the next user on this browser doesn't
    // inherit the previous user's prefs. The next login re-hydrates from
    // branding (per-domain defaults) and /user/{id}/ui/settings.
    try {
      localStorage.removeItem('obd3:theme');
    } catch {
      /* localStorage unavailable — no-op */
    }
  },

  popSession: () => {
    const restored = popPrevious();
    if (!restored) return false;
    set({
      userId: restored.user.userId,
      role: restored.user.role as UserRole,
      username: restored.user.username ?? null,
      name: restored.user.name ?? null,
      hasStack: hasStackedSession(),
    });
    return true;
  },

  switchTo: (payload) => {
    pushAndSwitchTo(payload);
    set({
      userId: payload.user.userId,
      role: payload.user.role as UserRole,
      username: payload.user.username ?? null,
      name: payload.user.name ?? null,
      hasStack: hasStackedSession(),
    });
  },
}));
